import { Field, Form as FormikForm } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Accordion, Col, Form, Row } from "react-bootstrap";
import * as yup from "yup";
import { useLayerService } from "../../../hooks";
import { CensusCategory } from "../../../types/Census/CensusCategory";
import { CensusOutputAreaType } from "../../../types/Census/CensusOutputAreaType";
import {
  CensusSelectedColumn,
  CensusSelectedColumnType,
} from "../../../types/Census/CensusSelectedColumn";
import { WfsGetFeatureRequest } from "../../../types/Wfs/WfsGetFeatureRequest";
import LoadingSpinner from "../../Loading/LoadingSpinner";
import { useDataSelectionContext } from "../DataSelectionContext";
import DataSelectionModel from "../DataSelectionModal";
import AgeBandSelection from "./AgeBandSelection";
import ColumnSelection from "./ColumnSelection";
import SelectedCensusColumns from "./SelectedCensusColumns";
import { useBoundarySelectContext } from "../../boundarySelection/BoundarySelectContext";
import { Extent, intersects } from "ol/extent";
import { BoundarySelection } from "../../../types/Boundary/BoundarySelection";

export interface CensusFormData {
  outputType: number;
  releaseYear: number | undefined;
  outputArea: number | undefined;
  categoryId: number;
  columns: CensusSelectedColumn[];
  columnId: number;
}

const Census = () => {
  const layerName = "basemap:census";
  const layerService = useLayerService();
  const { layers, getLayer, wfsOrder } = useDataSelectionContext();
  const layer = useMemo(() => getLayer(layerName), [layers]);
  const { boundaries } = useBoundarySelectContext();
  const [releaseYears, setReleaseYears] = useState<number[]>([]);
  const [outputAreaTypes, setOutputAreaTypes] = useState<
    CensusOutputAreaType[]
  >([]);
  const [categories, setCategories] = useState<CensusCategory[]>([]);
  const [ageBandCategory, setAgeBandCategory] = useState<
    CensusCategory | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState({
    releaseYears: true,
    outputAreas: false,
    categories: false,
  });

  const initialValues = useMemo(
    () =>
    ({
      outputType: 2,
      releaseYear: undefined,
      outputArea: undefined,
      categoryId: -1,
      columns: [],
      columnId: -1,
    } as CensusFormData),
    [layer]
  );

  const schema = useMemo(
    () =>
      yup.object().shape({
        releaseYear: yup.number().required(),
        outputType: yup.number().required(),
        outputArea: yup.number().required(),
        columns: yup
          .array()
          .of(
            yup.object().shape({
              value: yup.number().required(),
              label: yup.string().required(),
            })
          )
          .min(1)
          .required(),
      }),
    [layer]
  );
  const englandExtent = [-714522.7676150717, 6422893.150732635, 196334.81041443994, 7520905.547930526] as Extent;
  const scotlandExtent = [-962913.1311595398, 7291012.866813274, -80663.86298995547, 8593922.80525788] as Extent;

  useEffect(() => {
    boundaries.forEach((feature) => {
      setReleaseYears(getReleaseYearsFromBoundaries(boundaries));

    });
  }, [boundaries]);

  useEffect(() => {
    const fetchInitialData = async () => {
      let [years] = await Promise.all([layerService.GetCensusReleaseYears()]);
      const boundaryYears = getReleaseYearsFromBoundaries(boundaries);
      const yearToRemoved = [...years].filter(x => !boundaryYears.includes(x));
      setReleaseYears(yearToRemoved);
      setIsLoading({
        ...isLoading,
        releaseYears: false,
      });
    };

    fetchInitialData();
  }, [layerService]);


  const getReleaseYearsFromBoundaries = (boundaries: BoundarySelection[]) => {
    const years = new Set();
    boundaries.forEach((feature) => {
      const geom = feature.geometry;
      const extent = geom.getExtent();
      const isInEngland = intersects(extent, englandExtent);
      const isInScotland = intersects(extent, scotlandExtent);
      let isEngland = false;
      let isScotland = false;
      if (isInEngland) {
        years.add(2022);
        isEngland = true;
      }
      if (isInScotland) {
        years.add(2021);
        isScotland = true;
      }
      if (isEngland && isScotland) {
        years.clear();
      }
    });
    return Array.from(years) as number[];
  };

  const fetchCategoryData = async (releaseYear: number, outputAreaType: number) => {
    setIsLoading({ ...isLoading, categories: true });
    const response = await layerService.GetCensusCategories(releaseYear, outputAreaType);
    response.forEach((category) => {
      category.columns = category.columns.map((column) => ({
        ...column,
        categoryId: category.categoryId,
      }));
    });
    setCategories(response);
    setIsLoading({ ...isLoading, categories: false });
    setAgeBandCategory(response.find((x) => x.hasAgeBand));
  };

  const fetchOutputAreaTypes = async (releaseYear: number) => {
    setIsLoading({ ...isLoading, outputAreas: true });
    const response = await layerService.GetCensusOutputAreaTypes(releaseYear);
    setOutputAreaTypes(response);
    setIsLoading({ ...isLoading, outputAreas: false });
  };

  const handleSelectedColumnChange = (
    e: any,
    values: CensusFormData,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    let targetValue = e;

    if (targetValue === "" || targetValue === undefined) {
      return;
    }

    let selectedColumnId = parseInt(targetValue);
    let selectedCategoryId = values.categoryId;
    let selectedCategory = categories.find(
      (x) => x.categoryId === selectedCategoryId
    );

    if (selectedColumnId === -1) {
      if (
        values.columns.find(
          (column) =>
            column.type === CensusSelectedColumnType.Category &&
            column.value === selectedCategoryId
        ) !== undefined
      ) {
        return;
      }

      // Get existing individual columns for this category
      let existing = values.columns.filter(
        (x) =>
          x.type === CensusSelectedColumnType.Individual &&
          selectedCategory?.columns.find((y) => y.columnId === x.value) !==
          undefined
      );
      // Remove existing
      for (let i = 0; i < existing.length; i++) {
        values.columns = values.columns.filter(
          (x) => x.value !== existing[i].value
        );
      }

      values.columns = [
        {
          type: CensusSelectedColumnType.Category,
          value: selectedCategoryId,
          label: selectedCategory?.categoryName
            ? "All: " + selectedCategory.categoryName
            : "Error",
        },
        ...values.columns,
      ];
    } else {
      if (
        values.columns.find(
          (column) =>
            column.type === CensusSelectedColumnType.Individual &&
            column.value === selectedColumnId
        ) !== undefined
      ) {
        return;
      }

      values.columns = [
        {
          type: CensusSelectedColumnType.Individual,
          value: selectedColumnId,
          label:
            categories
              .flatMap((category) => category.columns)
              .find((column) => column.columnId === selectedColumnId)?.name ??
            "Error",
        },
        ...values.columns,
      ];
    }
    setFieldValue("columns", values.columns);
  };

  const onSubmit = (values: CensusFormData) => {

    let columnIds = values.columns.map((x) => {
      if (x.type === CensusSelectedColumnType.Category) {
        return categories
          .find((y) => y.categoryId === x.value)
          ?.columns.map((z) => z.columnId);
      } else if (x.type === CensusSelectedColumnType.Individual) {
        return x.value;
      } else {
        return undefined;
      }
    });

    columnIds = columnIds.flat().filter((x) => x !== undefined);

    let aggregateFilters = values.columns.map((x) => {
      if (
        x.type === CensusSelectedColumnType.AgeBand &&
        Array.isArray(x.value)
      ) {
        return x.value[0] + ":" + x.value[1];
      } else {
        return undefined;
      }
    });

    aggregateFilters = aggregateFilters.filter((x) => x !== undefined);

    let filter = "ReleaseYear = " + values.releaseYear;

    if (columnIds.length > 0) {
      filter += " AND ColumnId IN " + columnIds.join(",");
    }

    if (aggregateFilters.length > 0) {
      filter += " AND AgeBandValue AGGREGATE " + aggregateFilters.join(",");
    }

    filter += " AND OutputAreaType = " + values.outputArea;

    wfsOrder({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      resultType: "Results",
      typeNames: layerName,
      outputFormat:
        values.outputType === 1
          ? "excel"
          : values.outputType === 2
            ? "csv"
            : "shapefile",
      filter: filter,
    } as WfsGetFeatureRequest);
  };

  const handleAgeBandSelection = (
    ageBandFrom: number,
    ageBandTo: number,
    values: CensusFormData
  ) => {
    const newColumn = {
      type: CensusSelectedColumnType.AgeBand,
      value: [ageBandFrom, ageBandTo],
      label: `Age Band: ${ageBandFrom} to ${ageBandTo}`,
    };

    let existing = values.columns.filter(
      (x) =>
        x.type === CensusSelectedColumnType.AgeBand &&
        Array.isArray(x.value) &&
        x.value[0] === ageBandFrom &&
        x.value[1] === ageBandTo
    );

    if (existing.length > 0) {
      return;
    }

    values.columns = [newColumn, ...values.columns];
  };

  return (
    <>
      <DataSelectionModel
        headerText="Census"
        initialValues={initialValues}
        validationSchema={schema}
        size="lg"
        onSubmit={onSubmit}
        layer={layer}
      >
        {({ values, errors, setFieldValue }) => (
          <FormikForm>
            <Row className="justify-content-center">
              <Col className="col-md-10">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Release Year</Form.Label>
                  <div>
                    {!isLoading.releaseYears && (
                      <Form.Control
                        as="select"
                        name="releaseYear"
                        onChange={(e) => {
                          const selectedYear = parseInt(e.target.value);
                          setFieldValue("releaseYear", selectedYear);
                          setFieldValue("categoryId", -1);
                          setFieldValue("columnId", -1);
                          setFieldValue("columns", []);
                          setFieldValue("outputArea", undefined);
                          setAgeBandCategory(undefined);
                          setCategories([]);
                          fetchOutputAreaTypes(selectedYear);
                        }}
                      >
                        {values.releaseYear === undefined && (
                          <option value="">Select a release year</option>
                        )}
                        {releaseYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Form.Control>
                    )}
                    {isLoading.releaseYears && <LoadingSpinner />}
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col className="col-md-10">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">
                    Geographical Area Type
                  </Form.Label>
                  <div>
                    {!isLoading.outputAreas && (
                      <Form.Control
                        as="select"
                        name="outputArea"
                        onChange={(e) => {
                          const selectedOutputAreaType = parseInt(e.target.value);
                          setFieldValue("outputArea", selectedOutputAreaType);
                          setCategories([]);
                          values.releaseYear!==undefined && selectedOutputAreaType!==undefined && fetchCategoryData(values.releaseYear, selectedOutputAreaType);
                        }}
                        disabled={isLoading.releaseYears || !values.releaseYear}
                      >
                        {(isLoading.releaseYears || !values.releaseYear) && (
                          <option value="">Please select a release year</option>
                        )}
                        {(!isLoading.releaseYears) && (
                          <option value="">Select an output area</option>
                        )}
                        {outputAreaTypes.map((type) => (
                          <option
                            key={type.outputAreaTypeId}
                            value={type.outputAreaTypeId}
                          >
                            {type.name}
                          </option>
                        ))}
                      </Form.Control>
                    )}
                    {isLoading.outputAreas && <LoadingSpinner />}
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row className="justify-content-center mt-3">
              <Col className="col-md-10">
                <Form.Label className="mb-0 mt-2">Data Selection</Form.Label>
                <Accordion>
                  <ColumnSelection
                    isLoading={isLoading}
                    categories={categories}
                    handleSelectedColumnChange={handleSelectedColumnChange}
                    setFieldValue={setFieldValue}
                    values={values}
                  />
                  <AgeBandSelection
                    onAgeBandSelection={handleAgeBandSelection}
                    values={values}
                    setFieldValue={setFieldValue}
                    ageBandCategory={ageBandCategory}
                  />
                </Accordion>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col className="col-md-10">
                <SelectedCensusColumns
                  selectedColumns={values.columns}
                  onColumnClick={(e) => {
                    setFieldValue(
                      "columns",
                      values.columns.filter((x) => x.value !== e.value)
                    );
                  }}
                />
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col className="col-md-10">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Output Type</Form.Label>
                  <Field
                    as={Form.Select}
                    name="outputType"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFieldValue("outputType", parseInt(e.target.value));
                    }}
                  >
                    {[
                      { value: 1, label: "Excel" },
                      { value: 2, label: "CSV" },
                      { value: 3, label: "ShapeFile" },
                    ].map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Field>
                </Form.Group>
              </Col>
            </Row>
          </FormikForm>
        )}
      </DataSelectionModel>
    </>
  );
};

export default Census;
